<script setup>
import ChatCustomerTaskAction from "@frontJs/components/actions/projectManagement/tasks/ChatCustomerTaskAction.vue";
import DeleteCustomerTaskAction from "@frontJs/components/actions/projectManagement/tasks/DeleteCustomerTaskAction.vue";
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import ComboboxField from "@js/components/fields/ComboboxField.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import WysiwygField from "@js/components/fields/WysiwygField.vue";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { goBack, useRoute } from "@js/utils/route";
import { useUrlSearchParams } from "@vueuse/core";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";

const { customerTask, tenant, tenants, idea } = defineProps({
  customerTask: {
    required: false,
    type: Object,
  },
  tenant: {
    type: Object,
    required: false,
  },
  tenants: {
    required: true,
    type: Array,
  },
  idea: {
    required: false,
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const priorities = [
  { value: "low", label: t("label.low") },
  { value: "medium", label: t("label.medium") },
  { value: "high", label: t("label.high") },
];

const { route } = useRoute();

const params = useUrlSearchParams("history");

const isSubmitting = ref(false);

const selectedTab = ref(0);

const form = reactive({
  name: customerTask ? customerTask.name : "",
  description: customerTask ? customerTask.description : "",
  customerComment: customerTask ? customerTask.customerComment : "",
  priority: customerTask
    ? priorities.find((priority) => priority.value === customerTask.priority)
    : priorities[1],
  tenant: tenant
    ? tenants.find((innerTenant) => innerTenant.id === tenant.id)
    : customerTask
      ? tenants.find((innerTenant) => innerTenant.id === customerTask.tenantId)
      : tenants[0],
});

const validationSchema = object({
  name: string().required(t("error.required")),
  description: string().nullable(),
  customerComment: string().nullable(),
  priority: object().required(t("error.required")),
  tenant: object().required(t("error.required")),
});

function changeTab(index) {
  selectedTab.value = index;
}

function saveCustomerTask() {
  isSubmitting.value = true;

  fetch(
    route(
      "front.projectManagement.task.postCustomerTask",
      customerTask
        ? {
            id: customerTask.id,
          }
        : {},
    ),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        name: form.name,
        description: form.description,
        customerComment: form.customerComment,
        priority: form.priority.value,
        tenantId: form.tenant.id,
        isIdea: customerTask ? customerTask.isIdea : idea,
      }),
    },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then(() => {
      toast.success(t("toast.successfullySaved"), {
        position: "top-right",
      });

      goBack();
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(route("front.projectManagement.task.customerTasks"));
    });
}

function deleteCallback() {
  toast.success(t("toast.successfullyDeleted"), { position: "top-right" });
  router.visit(route("front.projectManagement.task.customerTasks"));
}
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    @submit="saveCustomerTask"
    v-slot="{ errors }"
  >
    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{ information: t('title.information') }"
    >
      <template #panels>
        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <ComboboxField
              v-model="form.tenant"
              :options="tenants"
              :label="t('label.customer')"
              field-name="tenant"
              field-id="tenant"
              filteredOptionKey="name"
              required
              :disabled="!!tenant"
            >
              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ComboboxField>

            <ListboxField
              v-model="form.priority"
              field-id="priority"
              field-name="priority"
              :label="t('label.priority')"
              :options="priorities"
              required
            >
              <template #buttonContent>
                {{ form.priority.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <InputField
            v-model="form.name"
            :label="t('label.name')"
            :placeholder="t('label.name')"
            field-name="name"
            field-id="name"
            :max="138 - form.tenant.name.length"
            type="text"
            required
          >
            <template #inputPrefix> {{ form.tenant.name }} [CLIENT] -</template>
          </InputField>

          <WysiwygField
            v-model="form.description"
            :label="t('label.description')"
            field-name="description"
            field-id="description"
          />
        </TabPanel>
      </template>
    </TabGroup>

    <div
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <DeleteCustomerTaskAction
        v-if="customerTask"
        :customer-task="customerTask"
        :callback="deleteCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton type="secondary" :callback="() => setIsOpen(true)">
            {{ t("button.delete") }}
          </ActionButton>
        </template>
      </DeleteCustomerTaskAction>

      <ChatCustomerTaskAction
        v-if="customerTask"
        :tenant-task="customerTask"
        :default-open="params.toggle === 'chat'"
      >
        <template #button="{ setIsOpen }">
          <ActionButton
            ref="chat-button"
            type="primary"
            :callback="() => setIsOpen(true)"
            :ping="customerTask.hasNewCustomerAnswer"
          >
            {{ t("button.chat") }}
          </ActionButton>
        </template>
      </ChatCustomerTaskAction>

      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          <SpinnerIcon
            class="mr-1 size-4 animate-spin fill-white text-gray-200"
          />
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>

      <div v-if="Object.keys(errors).length > 0" class="text-sm text-red-500">
        {{ t("error.formHasErrors") }}
      </div>
    </div>
  </Form>
</template>
